
import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import ModalPortal from './ModalPortal';

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    children: React.ReactNode;
    width?: string;
    flush?: boolean;
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, children, width = 'max-w-2xl', flush = false }) => {
    const modalRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            const previousOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';

            return () => {
                document.removeEventListener('keydown', handleEscape);
                document.body.style.overflow = previousOverflow;
            };
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
      <ModalPortal>
        <div
            className="fixed inset-0 z-1000 flex items-center justify-center p-0 bg-black/60 backdrop-blur-xs sm:p-4"
            onClick={(event) => {
                if (event.target === event.currentTarget) onClose();
            }}
        >
            <div
                ref={modalRef}
                className={`max-h-dvh w-full ${width} bg-surface border-0 rounded-none shadow-2xl flex flex-col sm:h-auto sm:max-h-[90vh] sm:rounded-2xl sm:border sm:border-white/10`}
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between border-b border-white/5 p-3 sm:p-4">
                    <h3 className="text-lg font-bold text-white">{title}</h3>
                    <button
                        onClick={onClose}
                        className="p-1 text-muted hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className={`flex-1 overflow-y-auto scrollbar-thin ${flush ? 'p-0' : 'p-2 sm:p-4'}`}>
                    {children}
                </div>
            </div>
        </div>
      </ModalPortal>
    );
};

export default Modal;
